import { useEffect, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { AppState } from "@Store/index";
import wrapperHOC from "@Utils/wrapperHOC";
import useTranslationHook from "hooks/useTranslationHook";
import useWhitelabel from "hooks/useWhitelabel";
import getRecommendedForYou, { MINIMUM_TO_SHOW } from "@Pages/Home/helpers/RecommendedForYou";
import {
  clearHomeRailRegionCache,
  resolveRailRegionCanonical,
} from "@Pages/Home/helpers/HomeRailRegion";
import { logAppEvent } from "@Utils/appAnalytics";
import { APP_EVENTS, APP_PAGE } from "@Utils/webApputils/analyticEvents";
import { PAGE } from "constants/index";
import CollectionRail from "../CollectionRail";
import styles from "./recommendedProperties.module.scss";
import HomepageCollectionSkeleton from "../HomepageCollectionSkeleton";

interface Props {
  isDesktop?: boolean;
  /** Fires whenever the rail starts or stops rendering — see `Home`'s `railsShown`. */
  onVisibilityChange?: (isVisible: boolean) => void;
}

/**
 * "Recommended properties for you".
 *
 * 10 properties from `budget_city_rail` and 10 from `student_choice` for the region the user
 * last searched, interleaved and de-duplicated down to 10 cards. The rail hides below
 * `MINIMUM_TO_SHOW`. See `@Pages/Home/helpers/RecommendedForYou`.
 *
 * It is keyed on the *same* region as "Based on your <city> search" above it rather than the
 * second-most-recent search. Both rails rank the same region, so the overlap is real, but
 * keying this one elsewhere sent a user with one search to a city they never asked about — and
 * on desktop, where the rail above does not render, there is nothing to duplicate at all.
 * Duplicates *within* this rail are still merged out.
 */
const RecommendedProperties = ({ isDesktop = false, onVisibilityChange }: Props): JSX.Element => {
  const { t } = useTranslationHook();
  const { getFilteredRecentlyVisited, isWhitelabel, partner_id } = useWhitelabel();
  const [collection, setCollection] = useState<any[] | null>(null);
  const [cityCanonical, setCityCanonical] = useState<string>("");

  const { userLanguage, userCurrency } = useSelector(
    ({ appData }: AppState) => ({
      userLanguage: appData.userLanguage,
      userCurrency: appData.userCurrency,
    }),
    shallowEqual,
  );

  useEffect(() => {
    let recentSearch: any = null;
    try {
      [recentSearch] = (getFilteredRecentlyVisited() || []).filter((item: any) =>
        item?.canonical_name?.trim(),
      );
    } catch (error) {
      recentSearch = null;
    }

    let cancelled = false;
    (async () => {
      // The most recent region search, else the parent city of the property the user last
      // opened, else a top city — resolved once per Home load and shared with the rail above.
      const regionCanonicalName = await resolveRailRegionCanonical({
        recentSearch,
        isWhitelabel,
        partnerId: partner_id,
        userCurrency,
        useDefaultCity: true,
      });
      if (cancelled) return;
      setCityCanonical(regionCanonicalName);

      const items = await getRecommendedForYou({ regionCanonicalName, userLanguage, userCurrency });
      if (cancelled) return;
      // Below `MINIMUM_TO_SHOW` the render hides the rail; a failed fetch arrives as [].
      setCollection(items);
    })();

    return () => {
      cancelled = true;
      // Scoped to one Home load — see the matching cleanup in BasedOnYourSearch.
      clearHomeRailRegionCache();
    };
  }, []);

  // Same contract as the rail above: cards on the page, not a skeleton, count as shown.
  const isRailVisible = Boolean(collection && collection.length >= MINIMUM_TO_SHOW);
  useEffect(() => {
    onVisibilityChange?.(isRailVisible);
  }, [isRailVisible]);

  /** Mirrors `RecommendedForYouViewModel.handleCardTap` — no-ops outside the native app webview. */
  const handleCardTap = (item: any) => {
    logAppEvent({
      eventName: APP_EVENTS.CARD_CLICKED,
      payload: {
        card_name: item?.name,
        screen_name: APP_PAGE.HOME,
        section_name: "recommended_for_you",
        canonical_name: item?.canonical_name,
        property_id: String(item?.id ?? ""),
      },
    });
  };

  if (collection && collection.length < MINIMUM_TO_SHOW) return <></>;

  const title = t("home.recommendedproperties.title");
  const subTitle = t("home.recommendedproperties.subtitle");

  if (!collection) {
    return <HomepageCollectionSkeleton title={title} subTitle={subTitle} isDesktop={isDesktop} />;
  }

  const rail = (
    <CollectionRail
      key="recommendedProperties"
      id="recommendedProperties"
      collection={collection}
      title={title}
      subTitle={subTitle}
      pills={[]}
      showSeeMoreCard={false}
      recentlyViewedCityCanonical={cityCanonical}
      gtagEventAction="recommended_property_clicked"
      gtagEventCategory={PAGE.HOME}
      onItemClick={handleCardTap}
      isDesktop={isDesktop}
    />
  );

  // Desktop only, matching the rest of the home page's banding — `Subsection` also gates its grey
  // on `isDesktop`, and mobile has no alternating bands to slot into.
  return isDesktop ? <div className={styles.greyBand}>{rail}</div> : rail;
};

export default wrapperHOC(RecommendedProperties, {
  componentName: "RecommendedProperties-Home",
  showForChina: true,
  showForWhiteLable: false,
});
